// Live's "Generate captions" (#239 part 2, spec docs/specs/editor/captions.md): which clips are
// transcribed, the files sent (one `caption.transcribe` job a file), and each transcript's words
// placed on the video by its clip's trim and place, every clip's merged in time order. Pure: the
// jobs are `use-generate-captions.ts`'s.
import type { CaptionTranscript, SourceRef } from "../../../api/generated/model";
import { sourceKey } from "../media";
import { clipMsAt, fileMsAt } from "../speed-curve";
import { MAX_TRACKS, type AudioClip, type Timeline, type VideoClip } from "../timeline";
import type { Said } from "./Transcript";
import { MAX_VIDEO_MS, wordsIn, type TimedWord } from "./words";

export type SpokenClip = VideoClip | AudioClip;

/** Live's least length of a word placed by a clip (its 0.05 s). */
const MIN_WORD_MS = 50;

/**
 * Every video and sound on the timeline, as live's: muted, hidden or locked too, on any track.
 * A clip that plays the same file over the same time from the same point as one before it (a
 * video and its separated sound) is heard once, so its speech is not captioned twice (spec
 * decision 39; live's is).
 */
export function spokenClips(timeline: Timeline): SpokenClip[] {
  const heard = new Set<string>();
  const spoken: SpokenClip[] = [];
  for (const clip of timeline.clips) {
    if (clip.kind !== "video" && clip.kind !== "audio") continue;
    const same = `${sourceKey(clip.source)}|${clip.startMs}|${clip.endMs}|${clip.inMs}`;
    if (heard.has(same)) continue;
    heard.add(same);
    spoken.push(clip);
  }
  return spoken;
}

/** A file to transcribe: its key (`media.ts`'s) and the job's reference to it. */
export interface Transcribed {
  key: string;
  source: SourceRef;
}

/**
 * The files the clips play, each once: a file's transcript is the same for every clip of it, so
 * one job serves them all (live sends one call per clip, the whole file each time).
 */
export function transcribedSources(clips: readonly SpokenClip[]): Transcribed[] {
  const files = new Map<string, SourceRef>();
  for (const { source } of clips) {
    files.set(
      sourceKey(source),
      source.kind === "asset"
        ? { kind: "asset", asset_id: source.assetId }
        : { kind: "job_output", job_id: source.jobId, output_index: source.index },
    );
  }
  return [...files].map(([key, source]) => ({ key, source }));
}

/**
 * The transcripts' words on the video, live's way: a clip keeps the words its trim shows (any
 * that overlap it), each moved by the trim to the clip's place; a word cut by the trim's start
 * starts with the clip, one past its end runs on. A spacing token is dropped; a sound ElevenLabs
 * tagged ("(laughter)") is kept as words, live's. A token holding a space becomes its words,
 * sharing its time. Every clip's words, merged in time order; none from the video's 300 s on
 * (as an import's).
 */
export function wordsOnVideo(
  clips: readonly SpokenClip[],
  transcripts: ReadonlyMap<string, CaptionTranscript>,
): TimedWord[] {
  const words: TimedWord[] = [];
  for (const clip of clips) {
    const transcript = transcripts.get(sourceKey(clip.source));
    if (transcript === undefined) continue;
    // A clip at 2x reads twice its length of its file, and its words come twice as fast (spec
    // clip-speed); along a curve, each comes when the curve plays it (spec clip-speed-curves).
    const from = clip.inMs;
    const to = fileMsAt(clip, clip.endMs);
    for (const token of transcript.words) {
      if (token.kind === "spacing" || token.end_ms <= from || token.start_ms >= to) continue;
      const startMs = Math.round(clipMsAt(clip, Math.max(from, token.start_ms)));
      const endMs = Math.max(startMs + MIN_WORD_MS, Math.round(clipMsAt(clip, token.end_ms)));
      const parts = wordsIn(token.text);
      const span = endMs - startMs;
      parts.forEach((text, at) => {
        const start = startMs + Math.floor((span * at) / parts.length);
        const end = startMs + Math.floor((span * (at + 1)) / parts.length);
        words.push({ text, startMs: start, endMs: Math.max(end, start + 1), emphasis: token.highlight ?? null });
      });
    }
  }
  // A stable sort: two clips' words at one time keep the clips' order.
  return words.filter((word) => word.startMs < MAX_VIDEO_MS).sort((one, other) => one.startMs - other.startMs);
}

/** Why no caption track can be made, in a line, or null: a new one needs room among the tracks. */
export function noCaptionTrack(timeline: Timeline): Said | null {
  const room = timeline.tracks.some((track) => track.kind === "caption") || timeline.tracks.length < MAX_TRACKS;
  return room
    ? null
    : { tone: "error", text: `This project has the most tracks it can hold (${MAX_TRACKS}). Remove one to add captions.` };
}

/** Why a run cannot start on this timeline, in a line, or null. */
export function noCaptionRun(timeline: Timeline): Said | null {
  if (spokenClips(timeline).length === 0) {
    return { tone: "error", text: "No audio or video clips detected on the timeline." };
  }
  return noCaptionTrack(timeline);
}

/** A run asked for while one is going (the assistant's, beside the button's). */
export const CAPTIONS_BUSY: Said = { tone: "error", text: "Captions are already being generated." };
